// src/services/looker/lookerLayoutService.ts
/**
 * Reads and writes a workspace's /looker layout (table looker_layouts, migration
 * 109). RLS decides who may write — a workspace admin or a platform master — so
 * a refused save comes back as false, never as a silent success.
 *
 * @module services/looker/lookerLayoutService
 */
import { getSupabaseClient } from '@/config/supabase';
import { logError } from '@/services/logger';
import { parseLayout, type LookerLayoutEntry } from './lookerLayout';

/** The saved layout, or null when there is none (or it cannot be read) — the template order then applies. */
export const fetchLookerLayout = async (workspaceId: string, templateId: string): Promise<LookerLayoutEntry[] | null> => {
  const { data, error } = await getSupabaseClient()
    .from('looker_layouts')
    .select('tiles')
    .eq('workspace_id', workspaceId)
    .eq('template_id', templateId)
    .maybeSingle();
  if (error) {
    logError('LookerLayout', 'layout read failed', error);
    return null;
  }
  return data ? parseLayout((data as { tiles: unknown }).tiles) : null;
};

export const saveLookerLayout = async (
  workspaceId: string,
  templateId: string,
  tiles: readonly LookerLayoutEntry[],
): Promise<boolean> => {
  const { data, error } = await getSupabaseClient()
    .from('looker_layouts')
    .upsert({ workspace_id: workspaceId, template_id: templateId, tiles: tiles.map((e) => ({ id: e.id, width: e.width })) }, { onConflict: 'workspace_id,template_id' })
    .select('workspace_id');
  // An RLS refusal on upsert is an error; an empty result would be one too.
  if (error || !data || data.length === 0) {
    logError('LookerLayout', 'layout save failed', error ?? new Error('no row written'));
    return false;
  }
  return true;
};

/** Back to the template's own layout: the row's absence is the default. */
export const resetLookerLayout = async (workspaceId: string, templateId: string): Promise<boolean> => {
  const { error } = await getSupabaseClient()
    .from('looker_layouts')
    .delete()
    .eq('workspace_id', workspaceId)
    .eq('template_id', templateId);
  if (error) {
    logError('LookerLayout', 'layout reset failed', error);
    return false;
  }
  return true;
};
